/**
 * AVBP AI-Extraction SEAM (Phase 1 · Sprint 2).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHAT THIS IS — and what it is deliberately NOT
 * ─────────────────────────────────────────────────────────────────────────
 *  A verbatim message → derivative proposals. In production this is an LLM
 *  call (the locked stack: Anthropic for extraction reasoning, OpenAI
 *  Whisper upstream for voice→text). That production pipeline is a
 *  DOWNSTREAM sprint and is intentionally NOT built here.
 *
 *  This module defines:
 *    1. `Extractor` — the stable interface the real engine implements.
 *    2. `mockExtractor` — a DETERMINISTIC keyword extractor so the full
 *       capture → propose → commit/reject → ledger loop is exercisable
 *       end-to-end today, with zero network and zero API key.
 *
 *  Swap contract: replace `activeExtractor` with the LLM-backed
 *  implementation. Nothing else in the app changes — the Conversation
 *  screen depends only on the `Extractor` type. The seam is here, and
 *  only here, on purpose.
 *
 *  Posture rule encoded here (owner-locked): AI reply = ambient CHIP by
 *  default; a proposal only escalates to a BUBBLE (asksUser:true) when
 *  the AI needs a human decision (currently: add-participant). The mock
 *  reproduces that exact behaviour so the chip/bubble split is real in
 *  the demo, not faked in the view.
 * ─────────────────────────────────────────────────────────────────────────
 */
import type { ConvMessage, Proposal } from './conversation';

/**
 * A proposal as produced by extraction — pre-persistence.
 * `escalateAt` is intentionally NOT set here: extraction is pure and must
 * not mint Firestore Timestamps. Instead the extractor flags
 * `wantsEscalation`, and the persistence layer (postProposalsFromDrafts)
 * stamps the real 24-business-hour `escalateAt` Timestamp at write time.
 */
export type DraftProposal = Omit<
  Proposal,
  'id' | 'createdAt' | 'status' | 'escalateAt'
> & {
  /** true → persistence layer stamps escalateAt = now + 24 business hr. */
  wantsEscalation: boolean;
};

export interface Extractor {
  /** Pure: message in → draft proposals out. No side effects. */
  extract: (msg: ConvMessage) => DraftProposal[];
}

function srcLabel(m: ConvMessage): string {
  const time = new Date(m.localTs).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
  const bits = [`from ${m.authorName}`, time];
  if (m.attachments.length) {
    const photos = m.attachments.filter((a) => a.kind === 'photo').length;
    bits.push(photos ? `📎 ${photos} photo${photos > 1 ? 's' : ''}` : `📎 ${m.attachments.length} file`);
  }
  if (m.voice) bits.push('🎙 voice');
  return `↩ ${bits.join(' · ')}`;
}

/**
 * Deterministic keyword extractor. Same message → same proposals, always.
 * Rules are intentionally simple and legible — this is a demo seam, not the
 * production model. Each branch maps to one ProposalKind so every card
 * variant in the UI reference is reachable from typed input.
 */
export const mockExtractor: Extractor = {
  extract(m: ConvMessage): DraftProposal[] {
    const out: DraftProposal[] = [];
    const text = `${m.body} ${m.voice?.transcript ?? ''}`.toLowerCase();
    const src = srcLabel(m);

    // add_participant → ESCALATION BUBBLE (AI is asking the human).
    if (/\badd(ed)?\b.*\b(son|participant|architect|engineer|wife|partner)\b/.test(text)) {
      out.push({
        kind: 'add_participant',
        sourceMessageId: m.id,
        title: 'add_participant',
        detail:
          'A participant addition was requested in the conversation. Confirm role before adding — privacy is per-member.',
        payload: { suggestedRole: 'client_circle_coordination' },
        sourceLabel: src,
        wantsEscalation: false,
        asksUser: true,
      });
      return out; // a "needs your call" message is its own beat
    }

    // create_finding — defect / problem language.
    if (/\b(toilet|septic|crack|defect|problem|leak|damp)\b/.test(text)) {
      out.push({
        kind: 'create_finding',
        sourceMessageId: m.id,
        title: /toilet|septic/.test(text) ? 'toilet_placement' : 'structural_defect',
        detail: `Flagged from ${m.authorName}'s message${
          m.attachments.length ? ' + photo' : ''
        }. Needs review against the AUO ruleset.`,
        severity: 'needs remedy',
        payload: { rawExcerpt: m.body.slice(0, 140) },
        sourceLabel: src,
        wantsEscalation: true,
        asksUser: false,
      });
    }

    // create_finding — voice repair note.
    if (m.voice && /\b(uneven|repair|broken|fix|patch)\b/.test(text)) {
      out.push({
        kind: 'create_finding',
        sourceMessageId: m.id,
        title: 'repair_item',
        detail: `From voice note (${Math.round(m.voice.durationSec)}s), transcribed.`,
        severity: 'repair',
        payload: { transcript: m.voice.transcript },
        sourceLabel: src,
        wantsEscalation: true,
        asksUser: false,
      });
    }

    // update_intake — room / dimension language.
    const dim = m.body.match(/(\d{1,3})\s*[x×]\s*(\d{1,3})/i);
    if (dim || /\b(cabin|room|kitchen|bedroom|hall|office)\b/.test(text)) {
      out.push({
        kind: 'update_intake',
        sourceMessageId: m.id,
        title: 'room_update',
        detail: dim
          ? `Room dimension ${dim[1]}×${dim[2]} ft captured. Sets the room in the intake record.`
          : 'Room reference captured for the intake record.',
        payload: dim ? { w: Number(dim[1]), h: Number(dim[2]) } : {},
        sourceLabel: src,
        wantsEscalation: false,
        asksUser: false,
      });
    }

    // photo_tag — auto-tag attached photos.
    const photos = m.attachments.filter((a) => a.kind === 'photo');
    if (photos.length) {
      out.push({
        kind: 'photo_tag',
        sourceMessageId: m.id,
        title: `photo_tag ×${photos.length}`,
        detail: `${photos.length} photo${
          photos.length > 1 ? 's' : ''
        } auto-tagged to the discussed zone. Bind to the AUO view.`,
        payload: { count: photos.length },
        sourceLabel: src,
        wantsEscalation: false,
        asksUser: false,
      });
    }

    return out;
  },
};

/**
 * The extractor the app uses. Swap THIS binding for the LLM-backed
 * implementation when the production pipeline lands — no other file
 * changes. Keep the seam single.
 */
export const activeExtractor: Extractor = mockExtractor;
